import { InfiniteData, QueryKey, useInfiniteQuery } from '@tanstack/react-query';

import { get, RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { SearchParams, useSearchParam } from '@/domain/app';
import { useCreditCardDetails } from '@/domain/card';
import { KREDITTBANKEN_BASE_URL } from '@/react-query/common';

import { cardNumberIfOwner } from '@domain/account';
import { CreditCard, CreditCardTransaction, SimplePage } from '@generated-types';

const getCreditCardTransactionsByAccountId =
    (accountId: string, cardNumber?: CreditCard['cardNumber']) =>
    async ({ page }: { page: number }) => {
        const paramPairs = [
            ['page', `${page}`],
            ['size', '50'],
        ];

        if (cardNumber) {
            paramPairs.push(['cardNumber', cardNumber]);
        }
        const params = new URLSearchParams(paramPairs).toString();

        return get<SimplePage<CreditCardTransaction>>(
            `${KREDITTBANKEN_BASE_URL}/credit-card-accounts/${accountId}/transactions?${params}`,
        );
    };

export const useCreditCardTransactionsByAccount = () => {
    const accountId = useSearchParam(SearchParams.ACCOUNT_ID);
    const { data } = useCreditCardDetails();
    const cardNumber = cardNumberIfOwner(data);

    return useInfiniteQuery<
        SimplePage<CreditCardTransaction>,
        RequestExceptionWithResponse,
        InfiniteData<SimplePage<CreditCardTransaction>>,
        QueryKey,
        { page: number }
    >({
        queryKey: ['v4', 'credit-card-account', accountId, 'transactions', cardNumber],
        queryFn: ({ pageParam }) =>
            getCreditCardTransactionsByAccountId(accountId!, cardNumber)({ page: pageParam.page }),
        initialPageParam: { page: 1 },
        getNextPageParam: (lastPage) => {
            if (lastPage.hasNextPage) {
                return {
                    page: lastPage.page + 1,
                };
            }
            return undefined;
        },
        enabled: !!accountId,
    });
};
